import React from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Shield, AlertTriangle } from "lucide-react";

interface AdminRouteProps {
	children: React.ReactNode;
	fallbackPath?: string;
	showLoadingScreen?: boolean;
}

/**
 * AdminRoute component that protects admin-only routes
 * Automatically redirects non-admin users and shows loading states
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({
	children,
	fallbackPath = "/parent-dashboard",
	showLoadingScreen = true,
}) => {
	const { isAdmin, isLoading, error } = useAdminAuth(fallbackPath);

	// Show loading screen while checking authentication
	if (isLoading && showLoadingScreen) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
				<Card className="w-96 glass-card border-0 shadow-xl">
					<CardContent className="p-8 text-center">
						<div className="flex justify-center mb-4">
							<div className="relative">
								<Shield className="h-12 w-12 text-purple-600" />
								<Loader2 className="h-6 w-6 text-purple-400 animate-spin absolute -top-1 -right-1" />
							</div>
						</div>
						<h3 className="text-lg font-semibold text-gray-900 mb-2 font-karla">
							Verifying Admin Access
						</h3>
						<p className="text-sm text-gray-600 font-karla">
							Please wait while we verify your permissions...
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Show error screen if there's an authentication error
	if (error && showLoadingScreen) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
				<Card className="w-96 glass-card border-0 shadow-xl">
					<CardContent className="p-8 text-center">
						<div className="flex justify-center mb-4">
							<AlertTriangle className="h-12 w-12 text-red-600" />
						</div>
						<h3 className="text-lg font-semibold text-gray-900 mb-2 font-karla">
							Access Denied
						</h3>
						<p className="text-sm text-gray-600 font-karla mb-4">{error}</p>
						<p className="text-xs text-gray-500 font-karla">
							Redirecting to dashboard...
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Redirect if not admin (fallback for when loading screen is disabled)
	if (!isAdmin && !isLoading) {
		return <Navigate to={fallbackPath} replace />;
	}

	// Render children if admin access is confirmed
	if (isAdmin) {
		return <>{children}</>;
	}

	// Fallback - should not reach here normally
	return <Navigate to={fallbackPath} replace />;
};

/**
 * Higher-order component for wrapping admin routes
 */
export const withAdminAuth = <P extends object>(
	Component: React.ComponentType<P>,
	options?: {
		fallbackPath?: string;
		showLoadingScreen?: boolean;
	},
) => {
	const WrappedComponent: React.FC<P> = (props) => (
		<AdminRoute
			fallbackPath={options?.fallbackPath}
			showLoadingScreen={options?.showLoadingScreen}
		>
			<Component {...props} />
		</AdminRoute>
	);

	WrappedComponent.displayName = `withAdminAuth(${Component.displayName || Component.name})`;
	return WrappedComponent;
};

export default AdminRoute;
